/**
 * Servicio para consumir el API de shows (tnoradio-show-service).
 * Configurar REACT_APP_SHOWS_API_URL en .env (ej: https://api.shows.tnonetwork.com/api/shows).
 * Rutas usadas:
 * - GET .../details/:slug → detalle de un show
 * - GET .../enabled → lista de shows (misma fuente que On-Demand en tnonetwork)
 */
const DEFAULT_SHOWS_API_BASE = 'https://api.shows.tnonetwork.com/api/shows';

/** No listar en la grilla comercial del media kit (sigue con landing propia). */
const PROGRAM_GRID_EXCLUDED_SLUGS = new Set(['pedroconlasmascotas']);

function getBaseUrl() {
  const fromEnv = (process.env.REACT_APP_SHOWS_API_URL || '').trim().replace(/\/$/, '');
  if (fromEnv) return fromEnv;
  return DEFAULT_SHOWS_API_BASE.replace(/\/$/, '');
}

function isTruthyFlag(v) {
  return v === true || v === 1 || v === '1' || v === 'true';
}

/** Géneros extra solo para vistas que agrupan en cliente (p. ej. presentación Business Level). */
function hasEducationGenre(list) {
  return list.some((g) => /^educaci[oó]n$/i.test(String(g).trim()));
}

function applyPresentationGenreOverrides(showSlug, name, genreNames) {
  const out = [...genreNames];
  const slug = String(showSlug || '').toLowerCase().trim();
  const nm = String(name || '').toLowerCase();
  const isSomosPadres = /^somos-?padres$/i.test(slug) || /somos\s*padres/i.test(nm);
  if (isSomosPadres && !hasEducationGenre(out)) {
    out.push('Educación');
  }
  const isEnterateFlavia =
    /^enterateconflavia$/i.test(slug) ||
    /ent[eé]rate\s+con\s+flavia/i.test(nm) ||
    /pasaporte\s+social/i.test(nm);
  if (isEnterateFlavia) {
    return ['Turismo', GASTRONOMY_NICHE];
  }
  return out;
}

/** Géneros que no se listan en la presentación Business Level (agrupación cliente). */
function filterBannedPresentationGenres(genreNames) {
  return genreNames.filter((g) => {
    const t = String(g).trim();
    if (!t) return false;
    if (/^criminolog[ií]a$/i.test(t)) return false;
    if (/^criminology$/i.test(t)) return false;
    if (/^cultura$/i.test(t)) return false;
    return true;
  });
}

const ARTE_ESPECT_ENTRET = 'Arte, Espectáculos y Entretenimiento';
const MATERNITY_PARENTING_NICHE = 'De panza a brazos';
const GASTRONOMY_NICHE = 'Del fogón al micrófono';
const FASHION_NICHE = 'Pasarela al aire';

function isMaternityParentingShow(showSlug, name) {
  const slug = String(showSlug || '').toLowerCase().trim();
  const nm = String(name || '').toLowerCase();
  return (
    /^(frecuenciamaterna|madressinfiltro|somospadres|somos-?padres)$/i.test(slug) ||
    /frecuencia\s+materna|madres\s+sin\s+filtro|somos\s+padres/i.test(nm)
  );
}

/** No listar en el nicho «Entrevistas» (puede seguir en otros géneros del API, p. ej. Motivacional). */
function stripEntrevistasGenreForShow(showSlug, name, genreNames) {
  const slug = String(showSlug || '').toLowerCase().trim();
  const nm = String(name || '').toLowerCase();
  const isMujeresAdmirables =
    /^mujeresadmirables$/i.test(slug) || /mujeres\s+admirables/i.test(nm);
  if (!isMujeresAdmirables) return genreNames;
  return genreNames.filter((g) => !/^entrevistas$/i.test(String(g).trim()));
}

function normGenreKey(s) {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Agrupa géneros del API para la presentación Business Level (un bucket por familia). */
function presentationGenreBucket(raw) {
  const t = String(raw || '').trim();
  if (!t) return t;
  if (normGenreKey(t) === normGenreKey(ARTE_ESPECT_ENTRET)) return ARTE_ESPECT_ENTRET;
  if (/^arte\s+y\s+espect[áa]culos$/i.test(t)) return ARTE_ESPECT_ENTRET;
  if (/^comunidad$/i.test(t)) return 'Educación';
  if (/^formaci[oó]n\s+y\s+voz\s+profesional$/i.test(t)) return 'Educación';
  if (/^salud$/i.test(t)) return 'Zona Life';
  if (/gastronom[ií]a/i.test(t)) return GASTRONOMY_NICHE;
  if (/^moda$/i.test(t)) return FASHION_NICHE;
  if (/^mascotas(\s+y\s+veterinaria)?$/i.test(t)) return 'Zona Life';
  if (/^bienestar/i.test(t)) return 'Zona Life';
  if (/^maternidad|^crianza|^familia|^parental/i.test(t)) return MATERNITY_PARENTING_NICHE;
  if (/^historias\s+humanas$/i.test(t)) return 'Historias humanas';
  if (/^despu[eé]s\s+del\s+evento$/i.test(t)) return 'Historias humanas';
  if (/^deportes$/i.test(t)) return 'Informativo y reportajes';
  if (/^actualidad\s+y\s+periodismo$/i.test(t)) return 'Informativo y reportajes';
  if (/^econom[ií]a(\s+y\s+negocios)?$/i.test(t)) return 'Negocios, empresas y emprendimiento';
  if (/^negocios(\s+y\s+emprendimiento)?$/i.test(t)) return 'Negocios, empresas y emprendimiento';
  if (
    /^arte$/i.test(t) ||
    /^artes$/i.test(t) ||
    /^espect[áa]culos?$/i.test(t) ||
    /^entretenimiento/i.test(t) ||
    /^siempre\s+m[áa]s\s+m[úu]sica$/i.test(t)
  ) {
    return ARTE_ESPECT_ENTRET;
  }
  if (/^inmobiliaria$/i.test(t)) return 'Inmobiliario y comunidad';
  if (/^vida\s*inmobiliaria$/i.test(t)) return 'Inmobiliario y comunidad';
  if (/^alma\s*house(\s+radio)?$/i.test(t)) return 'Inmobiliario y comunidad';
  if (/^turismo$/i.test(t)) return 'Turismo';
  return t;
}

/** Show «Siempre más música» siempre cuenta en el bucket unificado (aunque el API no traiga ese género). */
function applySiempreMasMusicaShowOverride(showSlug, name, genreNames) {
  const slug = String(showSlug || '').toLowerCase().trim();
  const nm = String(name || '').toLowerCase();
  const isSmm =
    /siempre-?mas-?musica/i.test(slug) ||
    /siempremasmusica/i.test(slug.replace(/-/g, '')) ||
    /siempre\s+m[aá]s\s+m[uú]sica/i.test(nm);
  if (!isSmm) return genreNames;
  const out = [...genreNames];
  const alreadyInArteBucket = out.some((g) => presentationGenreBucket(String(g).trim()) === ARTE_ESPECT_ENTRET);
  if (!alreadyInArteBucket) out.push(ARTE_ESPECT_ENTRET);
  return out;
}

/**
 * Shows con isEnabled (misma regla que los carruseles por género en On-Demand en tnonetwork.com).
 * El hero de la portada usa además isActive; la grilla comercial lista todo el catálogo habilitado.
 * Fuente: GET .../enabled (tnoradio-show-service).
 * @returns {Promise<{ ok: boolean, groups: { genre: string, shows: { name: string, showSlug: string, isActive?: boolean }[] }[], error?: string }>}
 */
export async function fetchOndemandActiveEnabledByGenre() {
  const base = getBaseUrl();
  if (!base) {
    return { ok: false, groups: [], error: 'missing_base' };
  }
  const url = `${base}/enabled`;
  try {
    const response = await fetch(url);
    if (!response.ok) {
      return { ok: false, groups: [], error: `http_${response.status}` };
    }
    const data = await response.json();
    let shows = [];
    if (Array.isArray(data)) {
      shows = data;
    } else if (data && Array.isArray(data.shows)) {
      shows = data.shows;
    } else {
      return { ok: false, groups: [], error: 'unexpected_shape' };
    }

    const filtered = shows.filter((s) => {
      if (!s || !s.showSlug) return false;
      if (PROGRAM_GRID_EXCLUDED_SLUGS.has(String(s.showSlug).trim().toLowerCase())) return false;
      return isTruthyFlag(s.isEnabled);
    });

    const genreToShows = new Map();
    const seenByGenre = new Map();

    for (const raw of filtered) {
      const showSlug = String(raw.showSlug).trim();
      const name = String(raw.name || showSlug).trim() || showSlug;
      const genresArr = Array.isArray(raw.genres) ? raw.genres : [];
      let genreNames = genresArr
        .map((g) => (typeof g === 'string' ? g : g?.genre))
        .map((g) => (g && String(g).trim()) || '')
        .filter(Boolean);
      genreNames = applyPresentationGenreOverrides(showSlug, name, genreNames);
      genreNames = filterBannedPresentationGenres(genreNames);

      const isPuntoDeFeShow =
        /^puntodefe$/i.test(showSlug) ||
        /^punto-?de-?fe$/i.test(showSlug) ||
        /punto\s+de\s+fe/i.test(String(name || ''));
      if (isPuntoDeFeShow) {
        genreNames = genreNames.filter((g) => !/^educaci[oó]n$/i.test(String(g).trim()));
        if (!genreNames.length) genreNames = ['Lo divino y lo profano'];
      }

      genreNames = applySiempreMasMusicaShowOverride(showSlug, name, genreNames);

      const isComercioCorporativo =
        /^comerciocorporativo$/i.test(showSlug) || /comercio\s+corporativo/i.test(name);
      if (isComercioCorporativo) {
        genreNames = ['Negocios, empresas y emprendimiento'];
      }

      const isEnSintoniaSalud =
        /^ensintoniacontusalud$/i.test(showSlug) ||
        /en\s+sinton[ií]a\s+con\s+tu\s+salud/i.test(name);
      if (isEnSintoniaSalud) {
        genreNames = ['Zona Life'];
      }

      if (isMaternityParentingShow(showSlug, name)) {
        genreNames = [MATERNITY_PARENTING_NICHE];
      }

      genreNames = stripEntrevistasGenreForShow(showSlug, name, genreNames);

      const isPescandoLaModa =
        /^pescandolamoda$/i.test(showSlug) || /pescando\s+la\s+moda/i.test(name);
      if (isPescandoLaModa) {
        genreNames = [FASHION_NICHE];
      }

      const isSaludAlDia = /^saludaldia$/i.test(showSlug) || /salud\s+al\s+d[ií]a/i.test(name);
      if (isSaludAlDia) {
        genreNames = ['Segmentos y micros (rotación comercial)'];
      }

      const isSupercines =
        /^supercines$/i.test(showSlug) || /supercines/i.test(name);
      if (isSupercines) {
        genreNames = ['Segmentos y micros (rotación comercial)'];
      }

      const labels = genreNames.length ? genreNames : ['Sin género'];

      for (const g of labels) {
        const bucket = presentationGenreBucket(g);
        if (!genreToShows.has(bucket)) {
          genreToShows.set(bucket, []);
          seenByGenre.set(bucket, new Set());
        }
        const seen = seenByGenre.get(bucket);
        if (seen.has(showSlug)) continue;
        seen.add(showSlug);
        genreToShows.get(bucket).push({
          name,
          showSlug,
          isActive: isTruthyFlag(raw.isActive),
        });
      }
    }

    const groups = Array.from(genreToShows.entries())
      .map(([genre, list]) => ({
        genre,
        shows: list.sort((a, b) => a.name.localeCompare(b.name, 'es')),
      }))
      .sort((a, b) => a.genre.localeCompare(b.genre, 'es'));

    return { ok: true, groups, error: null };
  } catch (err) {
    if (process.env.NODE_ENV === 'development' && typeof console !== 'undefined' && console.warn) {
      console.warn('showService.fetchOndemandActiveEnabledByGenre', err);
    }
    return { ok: false, groups: [], error: 'fetch' };
  }
}

/** Parte la sinopsis ya en texto plano en unidades leíbles (oraciones o bloques por ; / —). */
function splitSynopsisSentences(plain) {
  const normalized = plain.replace(/\s+/g, ' ').trim();
  if (!normalized) return [];
  let units = normalized.split(/(?<=[.!?]["'»]?)\s+/).map((t) => t.trim()).filter(Boolean);
  if (units.length === 1 && normalized.length > 100) {
    const bySemi = normalized.split(/\s*;\s+/).map((t) => t.trim()).filter(Boolean);
    if (bySemi.length > 1) units = bySemi;
  }
  if (units.length === 1 && normalized.length > 100) {
    const byDash = normalized.split(/\s+—\s+|\s+-\s+/).map((t) => t.trim()).filter(Boolean);
    if (byDash.length > 1) units = byDash;
  }
  return units;
}

/** Líneas de agenda / promo que no resumen el contenido del show. */
function isPromoOrSchedulingLine(s) {
  const t = s.trim();
  if (t.length < 18) return true;
  return (
    /^(no te pierdas|no se lo pierda|sintoniz|disfruta|te invitamos|conéctate|conectate|por tno\b|tno network\b|en vivo por|transmitido|transmisión|horario|todos los (lunes|martes|miércoles|jueves|viernes|sábados|domingos)|de lunes a|a las\s+\d)/i.test(
      t
    ) || /^(visita|entra|suscríbete|suscribete|síguenos|siguenos)\b/i.test(t)
  );
}

/**
 * Resumen extractivo para tarjetas: lee la sinopsis en unidades (oraciones / bloques), omite promo/agenda
 * y conserva hasta dos frases sustantivas completas en orden. Evita cortar a la mitad; si hace falta,
 * elige una sola oración que quepa o la más corta entre las primeras; el UI puede usar line-clamp.
 *
 * @param {string} [text]
 * @param {number} [maxLen] — tope al unir dos oraciones (una sola oración puede superarlo para no trocearla)
 */
export function synopsisToBriefLine(text, maxLen = 220) {
  if (!text || typeof text !== 'string') return '';
  let s = text.replace(/<[^>]*>/g, ' ');
  s = s
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
    .replace(/&#x([0-9a-f]+);/gi, (_, h) => String.fromCharCode(parseInt(h, 16)));
  s = s.replace(/\s+/g, ' ').trim();
  if (!s) return '';

  const sentences = splitSynopsisSentences(s);
  if (!sentences.length) return '';

  let start = 0;
  while (start < sentences.length - 1 && isPromoOrSchedulingLine(sentences[start])) {
    start += 1;
  }

  const substantive = [];
  for (let i = start; i < sentences.length && substantive.length < 2; i += 1) {
    const sent = sentences[i].replace(/\s+/g, ' ').trim();
    if (!sent) continue;
    if (substantive.length === 0) {
      if (!isPromoOrSchedulingLine(sent)) substantive.push(sent);
      continue;
    }
    if (isPromoOrSchedulingLine(sent)) break;
    const joined = `${substantive[0]} ${sent}`;
    if (joined.length <= maxLen) substantive.push(sent);
    break;
  }

  let out = substantive.length ? substantive.join(' ') : '';
  if (!out) {
    const trimmed = sentences.map((t) => t.replace(/\s+/g, ' ').trim());
    const fallback = trimmed.find((t) => t && !isPromoOrSchedulingLine(t));
    out = (fallback || trimmed[start] || s).trim();
  }

  if (out.length > maxLen) {
    const pool = sentences
      .map((t) => t.replace(/\s+/g, ' ').trim())
      .filter((t) => t.length >= 36 && t.length <= maxLen && !isPromoOrSchedulingLine(t));
    if (pool.length) {
      out = pool.reduce((best, cur) => (cur.length < best.length ? cur : best));
    } else {
      out = (substantive[0] || sentences[start] || s).trim();
    }
  }

  return out.trim();
}

/**
 * Obtiene un show por slug (incluye synopsis, images, etc.).
 * @param {string} slug - showSlug (ej: 'puntodefe', 'ladosispositiva')
 * @returns {Promise<{ synopsis?: string, name?: string, showSlug?: string, images?: Array } | null>}
 */
export async function getShowBySlug(slug) {
  const base = getBaseUrl();
  if (!base) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('showService: REACT_APP_SHOWS_API_URL no configurado, no se puede cargar show por slug.');
    }
    return null;
  }
  const url = `${base}/details/${encodeURIComponent(slug)}`;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    return response.json();
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('showService: Error al cargar show', slug, err);
    }
    return null;
  }
}

const showService = { getShowBySlug, fetchOndemandActiveEnabledByGenre, synopsisToBriefLine };

export default showService;
